import styled, { keyframes } from 'styled-components';
import { media } from '@/styles/responsive';

export const Header = styled.header`
  background-color: ${(props) => props.theme.colors['primary-60']};
  height: 56px;
  position: fixed;
  width: 100%;
  z-index: 9;
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  color: #fff;
  padding: 0 24px;
  height: 100%;
  justify-content: space-between;

  ${media.mobile} {
    padding: 0 12px;
  }
`;

export const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  min-width: 0;
`;

export const TrioLogo = styled.img`
  height: 36px;
  margin-right: 20px;

  ${media.mobile} {
    height: 28px;
    margin-right: 12px;
  }
`;

export const OrgLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
  margin-right: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;

  ${media.mobile} {
    display: none;
  }
`;

export const ServicesButton = styled.button<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  background: ${(props) => (props.$isOpen ? 'rgba(255, 255, 255, 0.18)' : 'transparent')};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 300ms cubic-bezier(0.2, 0.8, 0.4, 1);
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.4);
  }

  ${media.mobile} {
    padding: 6px 8px;

    span {
      display: none;
    }
  }
`;

export const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 16px;

  ${media.mobile} {
    margin: 0 8px;
  }
`;

export const CurrentServiceBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.7;
  }

  span {
    color: #ffffff;
    font-weight: 600;
  }

  ${media.mobile} {
    gap: 4px;
    font-size: 13px;

    svg {
      display: none;
    }
  }
`;

/* ---- Services Dropdown Panel (Trio-branded) ---- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const DropdownBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: rgba(0, 0, 0, 0.12);
`;

export const DropdownPanel = styled.div`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  z-index: 11;
  background: ${({ theme }) => theme.colors['neutral-0']};
  border-top: 2px solid ${({ theme }) => theme.colors['primary-60']};
  box-shadow: 0 8px 24px hsla(0, 0%, 0%, 0.16);
  animation: ${fadeIn} 0.15s ease-out;
  max-height: calc(100vh - 56px);
  overflow-y: auto;
`;

export const DropdownInner = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 24px 32px 28px;

  ${media.mobile} {
    padding: 16px;
  }
`;

export const DropdownSearchRow = styled.div`
  margin-bottom: 24px;
  position: relative;
  display: flex;
  align-items: center;
  max-width: 360px;

  svg {
    position: absolute;
    left: 12px;
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors['neutral-50']};
    pointer-events: none;
  }
`;

export const DropdownSearchInput = styled.input`
  width: 100%;
  padding: 9px 14px 9px 38px;
  border: 1px solid ${({ theme }) => theme.colors['neutral-30']};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors['neutral-100']};
  background: ${({ theme }) => theme.colors['neutral-0']};
  outline: none;
  transition: border-color 300ms cubic-bezier(0.2, 0.8, 0.4, 1);

  &:focus {
    border-color: ${({ theme }) => theme.colors['primary-60']};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors['secondary-20']};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors['neutral-50']};
  }
`;

export const DropdownSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${({ theme }) => theme.colors['neutral-70']};
  margin-bottom: 12px;
`;

export const DropdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 4px;

  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const DropdownServiceCard = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors['neutral-100']};
  cursor: pointer;
  transition: background-color 300ms cubic-bezier(0.2, 0.8, 0.4, 1);

  &:hover {
    background-color: ${({ theme }) => theme.colors['secondary-20']};
    color: ${({ theme }) => theme.colors['neutral-100']};
    text-decoration: none;
  }

  &:active {
    background-color: ${({ theme }) => theme.colors['secondary-30']};
  }
`;

export const DropdownServiceIcon = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: ${({ $color }) => `${$color}14`};

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $color }) => $color};
  }
`;

export const DropdownServiceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DropdownServiceName = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors['neutral-100']};
`;

export const DropdownServiceDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors['neutral-70']};
  line-height: 1.3;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DropdownEmpty = styled.div`
  text-align: center;
  padding: 32px;
  color: ${({ theme }) => theme.colors['neutral-70']};
  font-size: 14px;
`;

export const RecentSection = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors['neutral-30']};
`;

